<template>
  <section
    class="gf-entity-card"
    :style="{ '--gf-entity-card-description-lines': descriptionLinesNumber }"
  >
    <div class="gf-entity-card__body">
      <div class="gf-entity-card__header">
        <div
          class="gf-entity-card__title"
          :class="[{ '--dark': isDarkHeader }, { '--short': isShortTitle }]"
          v-if="$slots?.headerTitle"
        >
          <slot name="headerTitle"></slot>
        </div>
        <div
          v-if="$slots?.actions || $slots?.dropdownActions || $slots?.additionalActions"
          class="gf-entity-card__actions"
        >
          <div
            class="gf-entity-card__first-actions"
            v-if="$slots?.actions || $slots?.dropdownActions"
          >
            <div
              class="gf-entity-card__short"
              v-if="$slots?.actions"
            >
              <slot name="actions"></slot>
            </div>
            <gf-dropdown
              v-if="$slots?.dropdownActions"
              position="right"
              btn-class="btn btn-sm text-muted p-0"
              btn-icon="gf-icon gf-icon-ellipsis-vertical"
              :no-carret="true"
              style="--gfd-minw: 100px; display: none"
            >
              <slot name="dropdownActions"></slot>
            </gf-dropdown>
          </div>
          <div
            class="gf-entity-card__second-actions"
            v-if="$slots?.additionalActions"
          >
            <slot name="additionalActions"></slot>
          </div>
        </div>
      </div>
      <div
        v-if="description && description?.length > 0"
        class="gf-entity-card__content"
      >
        {{ description }}
      </div>
    </div>
    <div
      class="gf-entity-card__footer"
      :class="{ '--split': splitFooterContent }"
      v-if="$slots?.footerContent || $slots?.footerActions || $slots?.mobileFooterActions"
    >
      <div
        v-if="$slots?.footerContent"
        class="gf-entity-card__content"
      >
        <slot name="footerContent"></slot>
      </div>
      <div
        v-if="$slots?.footerActions"
        class="gf-entity-card__actions"
      >
        <slot name="footerActions"></slot>
      </div>
      <div
        v-if="$slots?.mobileFooterActions"
        class="gf-entity-card__actions --mobile"
      >
        <slot name="mobileFooterActions"></slot>
      </div>
    </div>
  </section>
</template>

<script>
import GfDropdown from '@/shared-modules/shared-components/shared/GfDropdown';

export default {
  name: 'GfEntityCard',
  components: { GfDropdown },
  props: {
    description: {
      type: String,
      default: null
    },
    descriptionLinesNumber: {
      type: Number,
      default: 2
    },
    isDarkHeader: {
      type: Boolean,
      default: false
    },
    splitFooterContent: {
      type: Boolean,
      default: false
    },
    isShortTitle: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style lang="scss">
$this: '.gf-entity-card';

#{$this} {
  --gf-entity-card-description-lines: 2;
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  container-type: inline-size;
  container-name: card;

  #{$this}__body {
    display: flex;
    flex-direction: column;
    gap: 14px;

    #{$this}__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;

      #{$this}__title {
        width: 100%;
        max-width: calc(100% - 40px);
        display: flex;
        align-items: center;
        overflow-wrap: anywhere;
        gap: 8px;

        &.--short {
          @include theme.md {
            max-width: calc(100% - 150px);
          }
        }

        & .title {
          display: flex;
          gap: 4px;
          overflow: hidden;

          & > h6,
          & > span {
            line-height: 1.2;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        & h6 {
          color: var(--primary) !important;
          font-weight: 600 !important;

          &:hover {
            color: var(--primary-dark) !important;
          }
        }

        &.--dark h6,
        &.--dark h6 > a {
          color: var(--text) !important;

          &:hover {
            color: var(--primary-dark) !important;
          }
        }

        & .avatar {
          max-width: 50px;
          border: 1px solid var(--border-color);
          border-radius: 25px;
        }

        & .avatar-squared {
          max-width: 24px;
        }

        & .labels {
          display: flex;
          gap: 4px;
        }
      }

      #{$this}__actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;

        #{$this}__first-actions {
          display: flex;
          align-items: center;
          gap: 8px;

          #{$this}__short {
            display: none;

            @media screen and (min-width: 768px) {
              display: block;
            }
          }
        }

        #{$this}__second-actions {
          display: none;

          @media screen and (min-width: 768px) {
            display: block;
          }
        }
      }
    }

    #{$this}__content {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: var(--gf-entity-card-description-lines);
      line-clamp: car(--gf-entity-card-description-lines);
      -webkit-box-orient: vertical;
    }
  }
  #{$this}__footer {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;

    &.--split {
      margin-top: 14px;
      flex-direction: row;
      align-items: center;

      #{$this}__content,
      #{$this}__actions {
        margin-top: 0 !important;
      }
    }

    @container card (min-width: 550px) {
      flex-direction: row;
      align-items: flex-end;
      justify-content: space-between;
    }

    #{$this}__content {
      width: 100%;
      margin-top: 14px;
    }

    #{$this}__actions {
      margin-top: 14px;
      margin-left: auto;
      display: none;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
      text-wrap: nowrap;

      @media screen and (min-width: 768px) {
        @container card (min-width: 338px) {
          display: flex;
        }
      }

      &.--mobile {
        margin-top: 14px;
        display: flex !important;

        @media screen and (min-width: 768px) {
          @container card (min-width: 338px) {
            display: none !important;
          }
        }
      }
    }
  }
  & .mobile-label {
    display: flex;

    @media screen and (min-width: 768px) {
      @container card (min-width: 338px) {
        display: none !important;
      }
    }
  }
}
</style>
